import React, { useContext, useMemo, useState } from 'react';
import './SummonersRiftMap.css';
import { fmt, toSec, CAMPS, getWaveGold, getWaveXP } from './utils';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import { getCampImage, getObjectiveImage, getWaveMinionImages } from './jungleCampImages';

/** ===== Types ===== */
type Team = 'blue' | 'red' | 'neutral';
type BaseType = 'gromp' | 'blue' | 'wolves' | 'raptors' | 'red' | 'krugs' | 'scuttle';
type MapCampId =
  | 'blue-gromp' | 'blue-blue' | 'blue-wolves'
  | 'blue-raptors' | 'blue-red' | 'blue-krugs'
  | 'red-gromp' | 'red-blue' | 'red-wolves'
  | 'red-raptors' | 'red-red' | 'red-krugs'
  | 'scuttle-top' | 'scuttle-bot';

type ObjectiveId =
  | 'dragon'
  | 'voidgrubs'
  | 'herald'
  | 'baron'
  | 'atakhan-top'
  | 'atakhan-bot';

type MapCamp = {
  id: MapCampId;
  base: BaseType;
  team?: Exclude<Team, 'neutral'>; // undefined for scuttle
  name: string;
  x: number;  // % 0..100 (from your placer)
  y: number;  // % 0..100
  quadrant: string;
  initial: number;
  respawn: number;
};

type CampState = {
  nextSpawn: number;
  disabled: boolean;
  kills: number;
};

type Objective = {
  id: ObjectiveId;
  name: string;
  x: number;
  y: number;
  spawn: number;               // first spawn time
  respawn?: number;            // respawn time (undefined => none)
  oneShot?: boolean;           // once killed, never returns
  group?: 'atakhan';           // mutually exclusive group
};

type ObjectiveState = {
  nextSpawn: number;           // when it’s next available (or Infinity)
  killed: boolean;
  disabled: boolean;           // for exclusivity (e.g., Atakhan other pit)
};

/** ===== Constants & config ===== */
const CLEAR_TIME_DEFAULT = 15;            // seconds per take
const START_TIME_DEFAULT = toSec('1:30'); // jungle start
const WAVE_FIRST_SPAWN = toSec('1:00');
const WAVE_PERIOD = 30;

/** Camp coordinates (your latest placements) */
const MAP_CAMPS: MapCamp[] = [
  // Blue-side bottom jungle (your picture uses “blue team” on bottom-right)
  { id: 'blue-red', base: 'red', team: 'blue', name: 'Red Buff (Blue)', x: 48.08, y: 25.95, quadrant: 'Blue Bottom Quadrant', initial: CAMPS.red.initial, respawn: CAMPS.red.respawn },
  { id: 'blue-krugs', base: 'krugs', team: 'blue', name: 'Krugs (Blue)', x: 43.94, y: 16.76, quadrant: 'Blue Bottom Quadrant', initial: CAMPS.krugs.initial, respawn: CAMPS.krugs.respawn },
  { id: 'blue-raptors', base: 'raptors', team: 'blue', name: 'Raptors (Blue)', x: 52.49, y: 35.28, quadrant: 'Blue Bottom Quadrant', initial: CAMPS.raptors.initial, respawn: CAMPS.raptors.respawn },
  { id: 'blue-wolves', base: 'wolves', team: 'blue', name: 'Wolves (Blue)', x: 75.12, y: 43.26, quadrant: 'Blue Top Quadrant', initial: CAMPS.wolves.initial, respawn: CAMPS.wolves.respawn },
  { id: 'blue-blue', base: 'blue', team: 'blue', name: 'Blue Buff (Blue)', x: 75.21, y: 53.91, quadrant: 'Blue Top Quadrant', initial: CAMPS.blue.initial, respawn: CAMPS.blue.respawn },
  { id: 'blue-gromp', base: 'gromp', team: 'blue', name: 'Gromp (Blue)', x: 85.16, y: 57.24, quadrant: 'Blue Top Quadrant', initial: CAMPS.gromp.initial, respawn: CAMPS.gromp.respawn },

  // Red-side top jungle
  { id: 'red-red', base: 'red', team: 'red', name: 'Red Buff (Red)', x: 52.30, y: 74.05, quadrant: 'Red Top Quadrant', initial: CAMPS.red.initial, respawn: CAMPS.red.respawn },
  { id: 'red-krugs', base: 'krugs', team: 'red', name: 'Krugs (Red)', x: 56.90, y: 83.52, quadrant: 'Red Top Quadrant', initial: CAMPS.krugs.initial, respawn: CAMPS.krugs.respawn },
  { id: 'red-raptors', base: 'raptors', team: 'red', name: 'Raptors (Red)', x: 47.70, y: 64.72, quadrant: 'Red Top Quadrant', initial: CAMPS.raptors.initial, respawn: CAMPS.raptors.respawn },
  { id: 'red-wolves', base: 'wolves', team: 'red', name: 'Wolves (Red)', x: 25.45, y: 56.74, quadrant: 'Red Bottom Quadrant', initial: CAMPS.wolves.initial, respawn: CAMPS.wolves.respawn },
  { id: 'red-blue', base: 'blue', team: 'red', name: 'Blue Buff (Red)', x: 24.85, y: 46.48, quadrant: 'Red Bottom Quadrant', initial: CAMPS.blue.initial, respawn: CAMPS.blue.respawn },
  { id: 'red-gromp', base: 'gromp', team: 'red', name: 'Gromp (Red)', x: 14.95, y: 43.49, quadrant: 'Red Bottom Quadrant', initial: CAMPS.gromp.initial, respawn: CAMPS.gromp.respawn },

  // River Scuttles
  { id: 'scuttle-top', base: 'scuttle', name: 'Top Scuttle', x: 29.77, y: 35.50, quadrant: 'River (Top)', initial: CAMPS.scuttle.initial, respawn: CAMPS.scuttle.respawn },
  { id: 'scuttle-bot', base: 'scuttle', name: 'Bot Scuttle', x: 70.52, y: 64.89, quadrant: 'River (Bot)', initial: CAMPS.scuttle.initial, respawn: CAMPS.scuttle.respawn },
];

/** Objectives. Atakhan has two pits (mutually exclusive). Grubs + Herald are one-shot;
 * Baron has a respawn; Dragon respawns. Times are adjustable via props if you want later.
 */
const DEFAULT_OBJECTIVES: Objective[] = [
  // Bottom river: Dragon
  { id: 'dragon',   name: 'Dragon',     x: 67.14, y: 70.5, spawn: toSec('5:00'),  respawn: toSec('5:00') },
  // Top pit sequence
  { id: 'voidgrubs', name: 'Voidgrubs', x: 33.0, y: 29.51, spawn: toSec('5:00'),  oneShot: true },
  { id: 'herald',    name: 'Rift Herald', x: 33.0, y: 29.51, spawn: toSec('14:00'), oneShot: true },
  { id: 'baron',     name: 'Baron Nashor', x: 33.0, y: 29.51, spawn: toSec('25:00'), respawn: toSec('6:00') },
  // Atakhan (two possible pits, only one active)
  { id: 'atakhan-top', name: 'Atakhan', x: 20.66, y: 22.72, spawn: toSec('20:00'), oneShot: true, group: 'atakhan' },
  { id: 'atakhan-bot', name: 'Atakhan', x: 79.51, y: 77.31, spawn: toSec('20:00'), oneShot: true, group: 'atakhan' },
];

/** 
 * Wave lane paths with waypoints
 * Each waypoint represents a position along the lane
 * Waypoints are roughly 5 seconds of travel apart
 */
type WaveLaneWaypoint = { x: number; y: number; time: number }; // time in seconds from spawn

type WaveLane = {
  id: 'top' | 'mid' | 'bot';
  name: string;
  bluePath: WaveLaneWaypoint[];  // Blue team's path (spawn to enemy base)
  redPath: WaveLaneWaypoint[];   // Red team's path (spawn to enemy base)
  defaultMeetIndex: number;      // Index where waves typically meet (all turrets up)
};

/**
 * Lane paths - you can easily adjust these coordinates
 * Time values are approximate travel time from spawn (in seconds)
 * When all turrets are up, waves meet around the middle waypoint
 */
const WAVE_LANES: WaveLane[] = [
  { 
    id: 'top', 
    name: 'Top Lane',
    defaultMeetIndex: 3, // Waves meet at 4th waypoint when all turrets up
    bluePath: [
      { x: 80, y: 8, time: 0 },   // Blue nexus area
      { x: 70, y: 8, time: 5 },
      { x: 60, y: 8, time: 10 },
      { x: 50, y: 8, time: 15 },
      { x: 40, y: 8, time: 20 },  
      { x: 30, y: 8, time: 25 },
      { x: 17, y: 13, time: 30 },// Meeting point (middle of lane)

    ],
    redPath: [
      { x: 8, y: 90, time: 0 },   // Red nexus area
      { x: 8, y: 80, time: 5 },
      { x: 8, y: 70, time: 10 },
      { x: 8, y: 60, time: 15 },
      { x: 8, y: 50, time: 20 },  
      { x: 8, y: 40, time: 25 },
      { x: 14, y: 16, time: 30 }, // Meeting point (middle of lane)
    ],
  },
  { 
    id: 'mid', 
    name: 'Mid Lane',
    defaultMeetIndex: 3,
    bluePath: [
      { x: 90, y: 10, time: 0 },   // Blue nexus (bottom-right)
      { x: 80, y: 20, time: 5 },
      { x: 70, y: 30, time: 10 },
      { x: 60, y: 40, time: 15 },
      { x: 50, y: 50, time: 20 },  // Center
      { x: 40, y: 60, time: 25 },
      { x: 30, y: 70, time: 30 },  // Meeting point
    ],
    redPath: [
      { x: 10, y: 90, time: 0 },   // Red nexus (top-left)
      { x: 20, y: 80, time: 5 },
      { x: 30, y: 70, time: 10 },
      { x: 40, y: 60, time: 15 },
      { x: 50, y: 50, time: 20 },  // Center
      { x: 60, y: 40, time: 25 },
      { x: 70, y: 30, time: 30 },  // Meeting point
    ],
  },
  { 
    id: 'bot', 
    name: 'Bot Lane',
    defaultMeetIndex: 3,
    bluePath: [
      { x: 92, y: 20, time: 0 },   // Blue nexus area
      { x: 92, y: 30, time: 5 },
      { x: 92, y: 40, time: 10 },
      { x: 92, y: 50, time: 15 },
      { x: 92, y: 60, time: 20 },  
      { x: 92, y: 70, time: 25 },
      { x: 87, y: 83, time: 30 },  // Meeting point (middle of lane)
    ],
    redPath: [
      { x: 10, y: 92, time: 0 },   // Red nexus area
      { x: 20, y: 92, time: 5 },
      { x: 30, y: 92, time: 10 },
      { x: 40, y: 92, time: 15 },
      { x: 50, y: 92, time: 20 },  
      { x: 60, y: 92, time: 25 },
      { x: 84, y: 86, time: 30 },  // Meeting point (middle of lane)
    ],
  },
];

/**
 * Calculate wave position based on current time using waypoint interpolation
 * @param waveNumber - The wave number
 * @param team - 'blue' or 'red'
 * @param lane - The lane data with waypoints
 * @param currentTime - Current game time in seconds
 * @returns Position {x, y} as percentages, or null if wave not visible
 */
function calculateWavePosition(
  waveNumber: number,
  team: 'blue' | 'red',
  lane: WaveLane,
  currentTime: number
): { x: number; y: number } | null {
  const waveSpawnTime = WAVE_FIRST_SPAWN + (waveNumber - 1) * WAVE_PERIOD;
  
  // Wave hasn't spawned yet
  if (currentTime < waveSpawnTime) {
    return null;
  }
  
  // Time since this wave spawned
  const elapsed = currentTime - waveSpawnTime;
  
  // Wave cleared/despawned after 60s (for now - will be dynamic with wave logic later)
  if (elapsed > 60) {
    return null;
  }
  
  // Get the appropriate path for this team
  const path = team === 'blue' ? lane.bluePath : lane.redPath;
  
  // Find which waypoint segment we're in
  let segmentStart = path[0];
  let segmentEnd = path[path.length - 1];
  
  for (let i = 0; i < path.length - 1; i++) {
    if (elapsed >= path[i].time && elapsed < path[i + 1].time) {
      segmentStart = path[i];
      segmentEnd = path[i + 1];
      break;
    }
  }
  
  // If we've passed the last waypoint, stay at the last position
  if (elapsed >= path[path.length - 1].time) {
    const lastWaypoint = path[path.length - 1];
    return { x: lastWaypoint.x, y: lastWaypoint.y };
  }
  
  // Interpolate between waypoints
  const segmentDuration = segmentEnd.time - segmentStart.time;
  const segmentProgress = segmentDuration > 0 
    ? (elapsed - segmentStart.time) / segmentDuration 
    : 0;
  
  const x = segmentStart.x + (segmentEnd.x - segmentStart.x) * segmentProgress;
  const y = segmentStart.y + (segmentEnd.y - segmentStart.y) * segmentProgress;
  
  return { x, y };
}

/** ===== Component ===== */
type EventRow = {
  at: number; 
  name: string; 
  where: string; 
  nextSpawn?: number;
  campBase?: BaseType;
  objectiveId?: ObjectiveId;
};

export default function SummonersRiftMap({
  clearSeconds = CLEAR_TIME_DEFAULT,
  startAt = START_TIME_DEFAULT,
  mapSrc,
  objectives = DEFAULT_OBJECTIVES,
  atakhanSite = 'top', // 'top' or 'bot' => which pit is enabled
}: {
  clearSeconds?: number;
  startAt?: number;
  mapSrc?: string;
  objectives?: Objective[];
  atakhanSite?: 'top' | 'bot';
}) {
  const { gameWideAssets } = useContext(GameAssetsContext) || {};
  const mapImage = gameWideAssets?.summonersRiftMap;

  // time + events
  const [now, setNow] = useState<number>(startAt);
  const [events, setEvents] = useState<EventRow[]>([]);

  // camps
  const [campStates, setCampStates] = useState<Record<MapCampId, CampState>>(() => {
    const o: Record<MapCampId, CampState> = {} as any;
    for (const c of MAP_CAMPS) o[c.id] = { nextSpawn: c.initial, disabled: false, kills: 0 };
    return o;
  });

  // objectives
  const [objectiveStates, setObjectiveStates] = useState<Record<ObjectiveId, ObjectiveState>>(() => {
    const base: Record<ObjectiveId, ObjectiveState> = {} as any;
    for (const obj of objectives) {
      base[obj.id] = {
        nextSpawn: obj.spawn,
        killed: false,
        disabled: false,
      };
    }
    // Atakhan exclusivity: only one pit enabled
    base['atakhan-top'] && (base['atakhan-top'].disabled = atakhanSite === 'bot');
    base['atakhan-bot'] && (base['atakhan-bot'].disabled = atakhanSite === 'top');
    return base;
  });

  const wave = useMemo(() => {
    const n = Math.max(1, Math.floor((now - WAVE_FIRST_SPAWN) / WAVE_PERIOD) + 1);
    const windowStart = WAVE_FIRST_SPAWN + (n - 1) * WAVE_PERIOD;
    const arrives = windowStart + 30;
    return { n, spawn: windowStart, arrive: arrives, clear: arrives + 30 };
  }, [now]);

  /** ===== Camp interaction ===== */
  function handleCampClick(e: React.MouseEvent, camp: MapCamp) {
    e.preventDefault();
    if (e.type === 'contextmenu') {
      setCampStates((cs) => ({ ...cs, [camp.id]: { ...cs[camp.id], disabled: !cs[camp.id].disabled } }));
      return;
    }
    setCampStates((prev) => {
      const s = prev[camp.id];
      if (s.disabled) return prev;

      const startTime = Math.max(now, s.nextSpawn);
      const endTime = startTime + clearSeconds;
      let nextSpawn = endTime + camp.respawn;

      // Scuttle pair special: after both first scuttles die, next spawn is 2:30 after the second death.
      if (camp.base === 'scuttle') {
        const otherId: MapCampId = camp.id === 'scuttle-top' ? 'scuttle-bot' : 'scuttle-top';
        const other = prev[otherId];
        const thisKills = s.kills + 1;
        const otherKills = other.kills;
        if ((thisKills >= 1 && otherKills >= 1) && (s.kills === 0 || other.kills === 0)) {
          nextSpawn = endTime + CAMPS.scuttle.respawn;
        }
      }

      setEvents((ev) => [...ev, { at: endTime, name: camp.name, where: 'Camp', nextSpawn, campBase: camp.base }]);
      setNow(endTime);

      return {
        ...prev,
        [camp.id]: { nextSpawn, disabled: s.disabled, kills: s.kills + 1 },
      };
    });
  }

  /** ===== Objective interaction ===== */
  function handleObjectiveClick(e: React.MouseEvent, obj: Objective) {
    e.preventDefault();
    if (e.type === 'contextmenu') {
      setObjectiveStates((os) => ({ ...os, [obj.id]: { ...os[obj.id], disabled: !os[obj.id].disabled } }));
      return;
    }
    setObjectiveStates((prev) => {
      const s = prev[obj.id];
      if (s.disabled) return prev;

      // If we’re early, we wait until spawn.
      const startTime = Math.max(now, s.nextSpawn);
      const endTime = startTime + clearSeconds;

      // Determine next availability
      let nextSpawn: number | undefined = undefined;
      if (obj.oneShot) {
        nextSpawn = Infinity; // never again
      } else if (obj.respawn) {
        nextSpawn = endTime + obj.respawn;
      }

      // Log + advance time
      setEvents((ev) => [...ev, { at: endTime, name: obj.name, where: 'Objective', nextSpawn, objectiveId: obj.id }]);
      setNow(endTime);

      // Exclusivity for Atakhan: taking one disables both pits permanently
      const updated = { ...prev };
      if (obj.group === 'atakhan') {
        const otherId = obj.id === 'atakhan-top' ? 'atakhan-bot' : 'atakhan-top';
        if (updated[otherId]) updated[otherId] = { ...updated[otherId], disabled: true, killed: true, nextSpawn: Infinity };
      }

      updated[obj.id] = { nextSpawn: nextSpawn ?? Infinity, killed: true, disabled: s.disabled };
      return updated;
    });
  }

  /** ===== Render ===== */
  return (
    <div className="rift-wrap">
      <div className="rift-header">
        <div className="time">Time: <b>{fmt(now)}</b></div>
        <div className="wave">Wave: <b>{wave.n}</b> <span className="muted">({fmt(wave.spawn)} spawn → {fmt(wave.arrive)} arrive)</span></div>
        <div className="hint small">Left-click camps to take (+{clearSeconds}s). Right-click to toggle/deny.</div>
      </div>

      {/* Time scrubber */}
      <div className="time-scrubber-wrap card">
        <div className="scrubber-header">
          <span className="scrubber-label">Timeline Control</span>
          <button 
            className="scrubber-reset-btn"
            onClick={() => setNow(startAt)}
            title="Reset to start time"
          >
            Reset
          </button>
        </div>
        <div className="scrubber-slider">
          <span className="scrubber-time start">{fmt(startAt)}</span>
          <input
            type="range"
            min={startAt}
            max={startAt + 600} // 10 minutes max
            step={1}
            value={now}
            onChange={(e) => setNow(Number(e.target.value))}
            className="time-slider"
          />
          <span className="scrubber-time end">{fmt(startAt + 600)}</span>
        </div>
        <div className="scrubber-marks">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(min => (
            <button
              key={min}
              className="scrubber-mark"
              onClick={() => setNow(min * 60)}
              title={`Jump to ${min}:00`}
            >
              {min}m
            </button>
          ))}
        </div>
      </div>

      <div className="rift" onContextMenu={(e)=>e.preventDefault()}>
        {/** Map image from context */}
        {mapImage && <img src={mapImage} alt="Summoners Rift" className="rift-bg" />}
        
        {/** Camps */}
          {MAP_CAMPS.map((c) => {
            const s = campStates[c.id];
            const up = now >= s.nextSpawn;
            const timeUntil = s.nextSpawn - now;
            const soon = !up && timeUntil <= 10;
            const mid  = !up && timeUntil > 10 && timeUntil <= 30;
            const stateClass = up ? 'up' : soon ? 'soon' : mid ? 'mid' : 'down';
            const teamClass = c.team ? `team-${c.team}` : 'team-river';
            return (
              <button
                key={c.id}
                className={['node camp-node', stateClass, s.disabled ? 'disabled' : '', teamClass].join(' ')}
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
                title={`${c.name} • ${up ? 'UP' : `spawns in ${fmt(timeUntil)}`} • ${c.quadrant}`}
                onClick={(e)=>handleCampClick(e as any, c)}
                onContextMenu={(e)=>handleCampClick(e as any, c)}
              >
                <span className="ring" />
                <img src={getCampImage(c.base)} alt={c.name} className="camp-icon" />
                <span className="label">
                  <span className="name">{shortCampName(c.base)}</span>
                  {up ? <span className="status up-text">UP</span> : <>
                    <span className="status timer">{fmt(timeUntil)}</span>
                    <span className="respawns-at">→ {fmt(s.nextSpawn)}</span>
                  </>}
                </span>
              </button>
            );
          })}

          {/** Objectives */}
          {objectives.map((o) => {
            const s = objectiveStates[o.id];
            if (!s) return null;
            const up = now >= s.nextSpawn && !s.killed && !s.disabled;
            const timeUntil = s.nextSpawn - now;
            const soon = !up && !s.killed && !s.disabled && timeUntil <= 10;
            const mid  = !up && !s.killed && !s.disabled && timeUntil > 10 && timeUntil <= 30;
            const stateClass =
              s.disabled ? 'disabled' : s.killed ? 'killed' : up ? 'up' : soon ? 'soon' : mid ? 'mid' : 'down';

            return (
              <button
                key={o.id}
                className={['node obj-node', stateClass, objectiveKindClass(o.id)].join(' ')}
                style={{ left: `${o.x}%`, top: `${o.y}%` }}
                onClick={(e)=>handleObjectiveClick(e as any, o)}
                onContextMenu={(e)=>handleObjectiveClick(e as any, o)}
                title={
                  s.killed ? `${o.name} • taken`
                  : s.disabled ? `${o.name} • disabled`
                  : up ? `${o.name} • UP`
                  : `${o.name} • spawns in ${fmt(timeUntil)}`
                }
              >
                <span className="ring" />
                <img src={getObjectiveImage(o.id)} alt={o.name} className="obj-icon" />
                <span className="label">
                  <span className="name">{o.name}</span>
                  {s.killed ? <span className="status killed-text">TAKEN</span> :
                   s.disabled ? <span className="status disabled-text">DISABLED</span> :
                   up ? <span className="status up-text">UP</span> :
                   <>
                     <span className="status timer">{fmt(timeUntil)}</span>
                     <span className="respawns-at">→ {fmt(s.nextSpawn)}</span>
                   </>
                  }
                </span>
              </button>
            );
          })}

          {/** Waves - showing active waves (up to 2 per lane, one per team) */}
          {WAVE_LANES.flatMap((lane) => {
            const currentWaveNum = wave.n;
            const waves: JSX.Element[] = [];
            
            // Show current and previous wave (allows 2 waves active per lane)
            const waveNumsToShow = [currentWaveNum, currentWaveNum - 1].filter(n => n >= 1);
            
            // For each wave number, show both blue and red team waves
            waveNumsToShow.forEach(waveNum => {
              ['blue' as const, 'red' as const].forEach(team => {
                const position = calculateWavePosition(waveNum, team, lane, now);
                
                // Skip if wave is not visible
                if (!position) return;
                
                const hasCannonMinion = waveNum >= 3 && waveNum % 3 === 0;
                const gold = getWaveGold(waveNum);
                const xp = getWaveXP(waveNum);
                const minionImages = getWaveMinionImages(waveNum, team);
                
                waves.push(
                  <div
                    key={`${lane.id}-${waveNum}-${team}`}
                    className={['wave-node', hasCannonMinion ? 'cannon-wave' : '', `team-${team}`].join(' ')}
                    style={{ left: `${position.x}%`, top: `${position.y}%` }}
                    title={`Wave ${waveNum} (${team.toUpperCase()}) - ${lane.name}\n${gold.total}g • ${xp.total} XP\nSpawn: ${fmt(WAVE_FIRST_SPAWN + (waveNum - 1) * WAVE_PERIOD)}`}
                  >
                    {/* Outer ring encompassing all minions */}
                    <span className="wave-ring" />
                    
                    {/* Individual minion images in a circular pattern */}
                    <div className="minion-group">
                      {minionImages.map((imgSrc, idx) => {
                        // Position minions in a circular pattern
                        const angle = (idx / minionImages.length) * 2 * Math.PI - Math.PI / 2;
                        // Use percentage-based radius that scales with the wave-node size
                        const radiusPercent = 40; // % of container
                        const x = Math.cos(angle) * radiusPercent;
                        const y = Math.sin(angle) * radiusPercent;
                        
                        return (
                          <img
                            key={idx}
                            src={imgSrc}
                            alt={`Minion ${idx + 1}`}
                            className="srf-minion-icon"
                            style={{
                              left: `calc(50% + ${x}%)`,
                              top: `calc(50% + ${y}%)`,
                            }}
                          />
                        );
                      })}
                    </div>
                    
                    {/* Wave label with info */}
                    <span className="wave-label">
                      <span className="wave-number">
                        W{waveNum}
                        {hasCannonMinion && <span className="cannon-badge">🎯</span>}
                      </span>
                      <span className="wave-gold">{gold.total}g</span>
                    </span>
                  </div>
                );
              });
            });
            
            return waves;
          })}
      </div>

      {/* Timeline */}
      <div className="timeline card">
        <div className="timeline-head">Event Timeline</div>
        {events.length === 0 ? (
          <div className="empty muted">No events yet. Click a camp/objective.</div>
        ) : (
          <table className="tl-table">
            <thead>
              <tr>
                <th>Time</th><th>Camp</th><th>What</th><th>Type</th><th>Next Spawn</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={i}>
                  <td><b>{fmt(ev.at)}</b></td>
                  <td>
                    {ev.campBase && (
                      <img src={getCampImage(ev.campBase)} alt={ev.name} className="timeline-icon" />
                    )}
                    {ev.objectiveId && (
                      <img src={getObjectiveImage(ev.objectiveId)} alt={ev.name} className="timeline-icon" />
                    )}
                  </td>
                  <td>{ev.name}</td>
                  <td>{ev.where}</td>
                  <td>{ev.nextSpawn && ev.nextSpawn !== Infinity ? fmt(ev.nextSpawn) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** ===== helpers ===== */
function shortCampName(base: BaseType) {
  switch (base) {
    case 'blue': return 'Blue';
    case 'red': return 'Red';
    case 'gromp': return 'Gromp';
    case 'wolves': return 'Wolves';
    case 'raptors': return 'Raptors';
    case 'krugs': return 'Krugs';
    case 'scuttle': return 'Scuttle';
  }
}

function objectiveKindClass(id: ObjectiveId) {
  if (id === 'dragon') return 'obj-dragon';
  if (id === 'baron') return 'obj-baron';
  if (id === 'herald') return 'obj-herald';
  if (id === 'voidgrubs') return 'obj-grubs';
  if (id.startsWith('atakhan')) return 'obj-atakhan';
  return '';
}
